import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form, Field, useFormikContext } from "formik";
import { IMaskInput } from "react-imask";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@material-ui/core";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import BanksSelect from "../BanksSelect";
import ValidationUtils from "../../utils/classes/ValidationUtils";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: { display: "flex", flexWrap: "wrap", },

  buttonsContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "center",
    gap: "1em",
  },

  btnWrapper: { position: "relative", },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50.00%",
    left: "50.00%",
    marginTop: -12,
    marginLeft: -12,
  },

  floatingButton: {
    transition: "transform 0.30s",
    "&:hover": { transform: "translateY(-5px)", },
  },

  selectInputField: {
    "& .MuiSvgIcon-root": { color: theme.palette.text.primary, },
	},
}));

const PixKeyMask = React.forwardRef((props, ref) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const { ...other } = props;
  const { setFieldValue, values } = useFormikContext();

  const masks = useMemo(() => ({
    phoneNumber: {
      version1: "(00) 00000-0000",
      version2: "(00) 0000-0000 ",
    },
    cpf: "000.000.000-00",
    cnpj: "00.000.000/0000-00"
  }), []);

  const [currentMask, setCurrentMask] = useState("");



  //  *****************
  //  ** Use-Effects **
  //  *****************
  useEffect(() => {
    if (["email", "randomKey"].includes(values.type)) return;

    if (values.type !== "phoneNumber") setCurrentMask(masks[values.type]);
    else if (values.key.length === 11) setCurrentMask(masks[values.type].version1);
    else setCurrentMask(masks[values.type].version2);
  }, [masks, values.type, values.key]);



  //  ***************
  //  ** Functions **
  //  ***************
  const handleKeyDown = () => {
    const shouldUpdateMask = values.type === "phoneNumber" && values.key.length === 10;
    if (shouldUpdateMask) setCurrentMask(masks[values.type].version1);
  };  



  //  ************
  //  ** Return **
  //  ************
  if (["email", "randomKey"].includes(values.type)) {
    return (
      <input
        {...other}
        ref={ref}
        onChange={(event) => setFieldValue("key", event.target.value)}
      />
    );
  }

  return (
    <IMaskInput
      {...other}
      mask={currentMask}
      inputRef={ref}
      onKeyDown={handleKeyDown}
      onAccept={(value, _mask) => setFieldValue("key", value)}
      overwrite={false}
      unmask={true}
    />
  );
});

const PixMessagesModal = ({ open, onClose, pixMessageId }) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();

  const initialState = useMemo(() => {
    return {
      type: "randomKey",
      key: "",
      name: "",
      bankId: "",
      bankCustomName: ""
    }
  }, []);

  const [banks, setBanks] = useState([]);
  const [pixMessage, setPixMessage] = useState(initialState);
  const [isUsingBankCustomName, setIsUsingBankCustomName] = useState(false);



  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    if (!open) return;
    if (!pixMessageId) return;

    const fetchPixMessage = async () => {
      try {
        const { data } = await api.get(`/pixMessages/${pixMessageId}`);
        setPixMessage(data);
        setIsUsingBankCustomName(!data.bankId && data.bankCustomName);
      }
      catch (exception) {
        console.log("PixMessagesModal Use-Effect 1 Exception:", exception);
        toastError(exception);
      }
    };

    fetchPixMessage();
  }, [open, pixMessageId]);

  useEffect(() => {
    if (!open) return;

    const fetchBanks = async () => {
      try {
        const jsonBanksLocalStorage = JSON.parse(localStorage.getItem("jsonBanks"));
        const jsonBanksVersionLocalStorage = localStorage.getItem("jsonBanksVersion");
        const { data: jsonBanksVersionServer } = await api.get("/banksVersion");
        
        let banksData = [];

        if (jsonBanksVersionLocalStorage === jsonBanksVersionServer && jsonBanksLocalStorage?.length > 0) {
          banksData = [...jsonBanksLocalStorage];
        }
        else {
          const { data } = await api.get("/banks");
          banksData = [...data];
          localStorage.setItem("jsonBanks", JSON.stringify(data));
          localStorage.setItem("jsonBanksVersion", jsonBanksVersionServer);
        }

        setBanks(banksData);
      }
      catch (exception) {
        console.log("PixMessagesModal Use-Effect 2 Exception:", exception);
        toastError(exception);
      }
    };

    fetchBanks();
  }, [open]);



  //  ***************
  //  ** Functions **
  //  ***************
  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setBanks([]);
      setPixMessage(initialState);
      setIsUsingBankCustomName(false);
    }, 500);
  };

  const validatePixMessageValues = (values) => {
    // ***---- Empty Values ----***
    const isAnyValueEmpty = !values.type
      || !values.key
      || !values.name
      || (!values.bankId && !values.bankCustomName);

    if (isAnyValueEmpty) {
      toast.info(i18n.t("pixMessagesModal.validations.emptyValues"));
      return false;
    }

    // ***---- Type Structure ----***
    const isTypeInvalid = !["phoneNumber", "cpf", "cnpj", "email", "randomKey"].includes(values.type);

    if (isTypeInvalid) {
      toast.info(i18n.t("ERR_INVALID_PIX_MESSAGE_TYPE"));
      return false;
    }

    // ***---- Key Structure ----***
    const isKeyCpfInvalid = values.type === "cpf" && !ValidationUtils.validateCpf(values.key);
    const isKeyCnpjInvalid = values.type === "cnpj" && !ValidationUtils.validateCnpj(values.key);
    const isKeyPhoneNumberInvalid = values.type === "phoneNumber" && !ValidationUtils.validatePixPhoneNumber(values.key);
    const isKeyEmailInvalid = values.type === "email" && !ValidationUtils.validateEmail(values.key);

    if (isKeyCpfInvalid) toast.info(i18n.t("pixMessagesModal.validations.invalidCpf"));
    if (isKeyCnpjInvalid) toast.info(i18n.t("pixMessagesModal.validations.invalidCnpj"));
    if (isKeyPhoneNumberInvalid) toast.info(i18n.t("pixMessagesModal.validations.invalidPhoneNumber"));
    if (isKeyEmailInvalid) toast.info(i18n.t("pixMessagesModal.validations.invalidEmail"));
    
    const isKeyInvalid = isKeyCpfInvalid || isKeyCnpjInvalid || isKeyPhoneNumberInvalid || isKeyEmailInvalid;
    if (isKeyInvalid) return false;

    return true;
  };

  const handleSavePixMessage = async (values) => {
    try {
      if (!validatePixMessageValues(values)) return;

      values.isUsingBankCustomName = isUsingBankCustomName;
      
      if (pixMessageId) await api.put(`/pixMessages/${pixMessageId}`, values);
      else await api.post("/pixMessages", values);

      handleClose();
    }
    catch (exception) {
      console.log("Handle Save Pix Message Exception:", exception);
      toastError(exception);
    }
  };



  //  ************
  //  ** Return **
  //  ************
  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" scroll="paper" fullWidth>
        <DialogTitle>
          {pixMessageId ? i18n.t("pixMessagesModal.title.edit") : i18n.t("pixMessagesModal.title.add")}
        </DialogTitle>

        <Formik
          initialValues={pixMessage}
          enableReinitialize={true}
          onSubmit={(values, actions) => {
            setTimeout(async () => {
              await handleSavePixMessage(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting }) => (
            <Form>
              <DialogContent dividers>
                {/* 
                  **********
                  ** Type **
                  **********
                */}
                <FormControl
                  classes={{ root: classes.selectInputField }}
                  variant="outlined"
                  margin="dense"
                  error={touched.type && Boolean(errors.type)}
                  helperText={touched.type && errors.type}
                  fullWidth
                  required
                >
                  <InputLabel>{i18n.t("pixMessagesModal.form.type")}</InputLabel>
                  <Field
                    as={Select}
                    name="type"
                    label={i18n.t("pixMessagesModal.form.type")}
                  >
                    <MenuItem value="phoneNumber">{i18n.t("pixMessagesModal.form.types.phoneNumber")}</MenuItem>
                    <MenuItem value="cpf">{i18n.t("pixMessagesModal.form.types.cpf")}</MenuItem>
                    <MenuItem value="cnpj">{i18n.t("pixMessagesModal.form.types.cnpj")}</MenuItem>
                    <MenuItem value="email">{i18n.t("pixMessagesModal.form.types.email")}</MenuItem>
                    <MenuItem value="randomKey">{i18n.t("pixMessagesModal.form.types.randomKey")}</MenuItem>
                  </Field>
                </FormControl>

                {/* 
                  *********
                  ** Key **
                  *********
                */}
                <Field
                  as={TextField}
                  label={i18n.t("pixMessagesModal.form.key")}
                  autoComplete="off"
                  variant="outlined"
                  margin="dense"
                  name="key"
                  error={touched.key && Boolean(errors.key)}
                  helperText={touched.key && errors.key}
                  InputProps={{ inputComponent: PixKeyMask }}
                  onChange={() => {}}
                  autoFocus
                  fullWidth
                  required
                />
                
                {/* 
                  **********
                  ** Name **
                  **********
                */}
                <Field
                  as={TextField}
                  label={i18n.t("pixMessagesModal.form.name")}
                  autoComplete="off"
                  variant="outlined"
                  margin="dense"
                  name="name"
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  fullWidth
                  required
                />

                <br />
                <Divider />
                <br />


                  
                {/* 
                  ************************************
                  ** Check - Bank or BankCustomName **
                  ************************************
                */}
                <FormControlLabel
                  control={<Field as={Switch} color="primary" checked={isUsingBankCustomName} onChange={() => setIsUsingBankCustomName(previousValue => !previousValue)} />}
                  label={i18n.t("pixMessagesModal.form.isUsingBankCustomName")}
                />



                {/* 
                  ***********
                  ** Banks **
                  ***********
                */}
                {!isUsingBankCustomName && (
                  <BanksSelect banks={banks} />
                )}

                {/* 
                  **********************
                  ** Bank Custom Name **
                  **********************
                */}
                {isUsingBankCustomName && (
                  <Field
                    as={TextField}
                    label={i18n.t("pixMessagesModal.form.bankCustomName")}
                    autoComplete="off"
                    variant="outlined"
                    margin="dense"
                    name="bankCustomName"
                    error={touched.bankCustomName && Boolean(errors.bankCustomName)}
                    helperText={touched.bankCustomName && touched.bankCustomName}
                    fullWidth
                    required={isUsingBankCustomName}
                  />
                )}
              </DialogContent>

              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="inherit"
                  disabled={isSubmitting}
                  variant="outlined"
                  className={classes.floatingButton}
                >
                  {i18n.t("pixMessagesModal.buttons.cancel")}
                </Button>

                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={`${classes.btnWrapper} ${classes.floatingButton}`}
                >
                  {pixMessageId ? i18n.t("pixMessagesModal.buttons.okEdit") : i18n.t("pixMessagesModal.buttons.okAdd")}
                  {isSubmitting && (<CircularProgress size={24} className={classes.buttonProgress} />)}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default PixMessagesModal;
